import axios from 'axios';
import { getApiBase } from '../native/tenant';

// Which refresh endpoint the stored refresh token belongs to. Web (dispatch/
// admin) and MDT tokens are not interchangeable server-side, so the kind is
// recorded at login. Absent = an MDT app that logged in before web refresh
// existed, so keep the old behavior.
export type RefreshKind = 'web' | 'mobile';

const KIND_KEY = 'refresh_kind';

export function setSessionTokens(kind: RefreshKind, accessToken: string, refreshToken?: string) {
  localStorage.setItem('token', accessToken);
  localStorage.setItem(KIND_KEY, kind);
  if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
}

export function getRefreshKind(): RefreshKind {
  return localStorage.getItem(KIND_KEY) === 'web' ? 'web' : 'mobile';
}

export function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem(KIND_KEY);
  localStorage.removeItem('user');
  localStorage.removeItem('last_activity');
}

export interface SessionPolicy {
  dispatch_idle_timeout_minutes: number;
  mdt_idle_timeout_minutes: number;
  session_max_minutes: number;
}

export async function fetchSessionPolicy(): Promise<SessionPolicy | null> {
  try {
    const r = await axios.get(`${getApiBase()}/api/auth/session-policy`);
    return r.data as SessionPolicy;
  } catch {
    return null;
  }
}

// Shared across all callers so concurrent 401s only trigger one refresh.
let refreshing: Promise<string | null> | null = null;

export function refreshSession(): Promise<string | null> {
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) return Promise.resolve(null);
  if (!refreshing) {
    const path = getRefreshKind() === 'web' ? 'refresh' : 'mobile-refresh';
    refreshing = axios
      .post(`${getApiBase()}/api/auth/${path}`, { refresh_token: refresh })
      .then((r) => {
        const { access_token, refresh_token } = r.data || {};
        if (access_token) localStorage.setItem('token', access_token);
        if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
        return access_token || null;
      })
      .catch(() => null)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}
